import React, {useEffect, useRef, useState} from 'react';
import {Form, message,} from 'antd';
import type {ProFormInstance} from '@ant-design/pro-form';
import {wdiCore, WdiModalAppProps} from '@/components/WdiControls';
import {HorzLayoutOpts, WdiModalForm} from '@/components/WdiControls/WdiModalForm';
import {ResponseStatus} from '@/utils/request';
import {WdiSelect, WdiSelectFormat} from '@/components/WdiControls/WdiSelect';
import {Enum} from '@/enums/system';
import {SoldTo} from '@/services/mdm/soldto';
import {FormSp} from '@/services/sales/sp';
import {DataOptions} from "@/services/basic/metadata";
import {Supplier} from "@/services/mdm/supplier";
import {Company} from "@/services/mdm/company";
import {Staff} from "@/services/basic/staff";

const PREV_COLUMNS = {
  SOLD_TO_OPT: 1,
  SOLD_TO_REQ: 2,
  COMPANY_OPT: 4,
  SUPPLIER_OPT: 16,
  STAFF_OPT: 256,
}

const PM_FIELD_CONFIG: Record<number, number> = {
  [Enum.PaymentMethod.BILL_DEDUCT]: PREV_COLUMNS.SOLD_TO_OPT,
  [Enum.PaymentMethod.FEE_POOL]: PREV_COLUMNS.SOLD_TO_OPT,
  [Enum.PaymentMethod.FG_POOL]: PREV_COLUMNS.SOLD_TO_OPT,
  [Enum.PaymentMethod.BUY_GIVE_POOL]: PREV_COLUMNS.SOLD_TO_OPT,
  [Enum.PaymentMethod.ACCOUNT_DEDUCT]: PREV_COLUMNS.SOLD_TO_OPT | PREV_COLUMNS.SOLD_TO_REQ,
  [Enum.PaymentMethod.TRANSFER]: PREV_COLUMNS.SOLD_TO_OPT | PREV_COLUMNS.SOLD_TO_REQ | PREV_COLUMNS.COMPANY_OPT,
  [Enum.PaymentMethod.THIRD_PART]: PREV_COLUMNS.COMPANY_OPT | PREV_COLUMNS.SUPPLIER_OPT | PREV_COLUMNS.STAFF_OPT,
  [Enum.PaymentMethod.FREE_GOODS]: PREV_COLUMNS.SOLD_TO_OPT | PREV_COLUMNS.SOLD_TO_REQ,
  [Enum.PaymentMethod.ACCOUNTING_NO_ACCRUAL]: PREV_COLUMNS.SOLD_TO_OPT,
  [Enum.PaymentMethod.ACCOUNTING_ACCRUAL]: PREV_COLUMNS.SOLD_TO_OPT,
}

const PM_PAGE_TYPE_CONFIG: Record<number, number> = {
  [Enum.PaymentMethod.BILL_DEDUCT]: Enum.PageType.PAYMENT_BILL_DEDUCT,
  [Enum.PaymentMethod.FEE_POOL]: Enum.PageType.PAYMENT_BILL_DEDUCT,
  [Enum.PaymentMethod.FG_POOL]: Enum.PageType.PAYMENT_BILL_DEDUCT,
  [Enum.PaymentMethod.BUY_GIVE_POOL]: Enum.PageType.PAYMENT_BILL_DEDUCT,
  [Enum.PaymentMethod.ACCOUNT_DEDUCT]: Enum.PageType.PAYMENT_ACCOUNT_REDUCE,
  [Enum.PaymentMethod.TRANSFER]: Enum.PageType.PAYMENT_ACCOUNT_REDUCE,
  [Enum.PaymentMethod.THIRD_PART]: Enum.PageType.PAYMENT_THIRD_PART,
  [Enum.PaymentMethod.FREE_GOODS]: Enum.PageType.PAYMENT_FREE_GOODS,
  [Enum.PaymentMethod.ACCOUNTING_NO_ACCRUAL]: Enum.PageType.PAYMENT_BILL_DEDUCT,
  [Enum.PaymentMethod.ACCOUNTING_ACCRUAL]: Enum.PageType.PAYMENT_BILL_DEDUCT,
}

const PaymentPrevApp: React.FC<WdiModalAppProps> = (props) => {
  const formRef = useRef<ProFormInstance>();
  const [paymentMethodId, setPaymentMethodId] = useState<number>(0);
  const [payToTypeId, setPayToTypeId] = useState<number>(0);
  const [soldToCode, setSoldToCode] = useState<string>();
  const [companyCode, setCompanyCode] = useState<string>();

  const [currentStaff, setCurrentStaff] = useState<API.Staff>()

  useEffect(() => {
    (async () => {
      const resp = await Staff.current();
      setCurrentStaff(resp.data);
    })()
  }, []);

  const handlePayToTypeChange = (value: number) => {
    setPayToTypeId(value);
    if (Enum.PayToType.PAY_TO_STAFF == value) {
      formRef.current?.setFieldsValue({
        payToUserId: {
          value: currentStaff?.staffId,
          label: wdiCore.String.GenerateLabelDescription(currentStaff?.staffNoOrName, currentStaff?.title)
        }
      })
    }
  }
  return (
    <WdiModalForm
      {...HorzLayoutOpts}
      formRef={formRef}
      title={props.title}
      visible={props.visible}
      modalProps={{
        onCancel: () => {
          props.onClose?.()
        },
        okText: '下一步'
      }}
      onFinish={async (values) => {
        const {paymentMethodId, payToTypeId, soldToCode, companyCode, supplierId, payToUserId} = values;
        const entity: API.PaymentPrev = {
          formSaId: props.data.formSaId,
          paymentMethodId: WdiSelectFormat.getvalue(paymentMethodId),
          payToTypeId: WdiSelectFormat.getvalue(payToTypeId),
          soldToCode: WdiSelectFormat.getvalue(soldToCode),
          companyCode: WdiSelectFormat.getvalue(companyCode),
          supplierId: WdiSelectFormat.getvalue(supplierId),
          payToUserId: WdiSelectFormat.getvalue(payToUserId),
          formTypeId: Enum.FormType.ACTIVITY_PAYMENT,
          pageTypeId: PM_PAGE_TYPE_CONFIG[paymentMethodId],
          reimburseType: 1
        };
        const response: any = await FormSp.paymentPrev(entity);
        if (response.code != ResponseStatus.SUCCESS) {
          message.error(response.message);
        } else {
          if (props.onSave) {
            props.onSave(response);
          }
        }
      }}
    >
      <Form.Item
        name='paymentMethodId'
        label='支付方式'
        rules={[{required: true, message: '支付方式不能为空!'}]}>
        <WdiSelect {...{
          placeholder: '请选择支付方式',
          dataLoading: async (params) => {
            const response = await DataOptions.getPaymentMethodsByFormSaId(Number(props.data.formSaId));
            if (response.data.length == 1) {
              formRef.current?.setFieldsValue({paymentMethodId: response.data[0].value,})
              setPaymentMethodId(response.data[0].value);
            }
            return response.data?.map((item) => ({label: item.name, value: item.value}));
          },
          onChange: (value) => {
            setPaymentMethodId(value);
            formRef.current?.resetFields(["payToTypeId", "soldToCode", "companyCode", "supplierId", "payToUserId"])
          }
        }} />
      </Form.Item>
      <Form.Item
        name='payToTypeId'
        label='支付给'
        rules={[{required: true, message: '支付给不能为空!'}]}
        hidden={paymentMethodId != Enum.PaymentMethod.THIRD_PART}>
        <WdiSelect {...{
          disabled: paymentMethodId != Enum.PaymentMethod.THIRD_PART,
          placeholder: '请选择支付给',
          reloadKey: paymentMethodId,
          dataLoading: async () => {
            if (!paymentMethodId) {
              return [];
            }
            const params: API.DataOptionsFilter = {typeGroup: 172,};
            if (paymentMethodId != Enum.PaymentMethod.THIRD_PART) {
              params.inValues = String(Enum.PayToType.PAY_TO_SOLD_TO);
            } else {
              params.notInValues = String(Enum.PayToType.PAY_TO_SOLD_TO);
            }
            const response = await DataOptions.search(params);
            if (response.data.length == 1) {
              formRef.current?.setFieldsValue({payToTypeId: response.data[0].value});
              handlePayToTypeChange(response.data[0].value);
            }
            return response.data?.map((item) => ({label: item.name, value: item.value}));
          },
          onChange: (value) => {
            handlePayToTypeChange(value);
          }
        }} />
      </Form.Item>
      {!!(PM_FIELD_CONFIG[paymentMethodId] & PREV_COLUMNS.SOLD_TO_OPT) && (
        <Form.Item
          name="soldToCode" label="客户"
          rules={[{
            required: !!(PM_FIELD_CONFIG[paymentMethodId] & PREV_COLUMNS.SOLD_TO_REQ),
            message: '客户不能为空!'
          }]}>
          <WdiSelect {...{
            placeholder: "请选择客户",
            showSearch: true,
            dataLoading: async (params) => {
              const response = await SoldTo.searchForSp(Object.assign({
                formSaId: Number(props.data.formSaId),
                paymentMethodId: paymentMethodId,
              }, params));
              if (response?.data?.length == 1) {
                formRef.current?.setFieldsValue({
                  soldToCode: response?.data[0].soldToCode,
                })
                setSoldToCode(response?.data[0].soldToCode);
              }
              return response?.data?.map(item => ({value: item.soldToCode, label: item.codeOrName}));
            },
            onChange: (value) => {
              setSoldToCode(value);
            },
            reloadKey: paymentMethodId,
          }} />
        </Form.Item>
      )}
      {!!(PM_FIELD_CONFIG[paymentMethodId] & PREV_COLUMNS.COMPANY_OPT) && (
        <Form.Item
          name="companyCode"
          label="公司"
          rules={[{required: true, message: '公司不能为空!'}]}
        >
          <WdiSelect
            {...{
              placeholder: '请选择公司',
              showSearch: true,
              dataLoading: async (params) => {
                const response = await Company.searchForSp(Object.assign({
                  formSaId: props.data.formSaId,
                  paymentMethodId: paymentMethodId,
                  soldToCode: soldToCode,
                }, params));
                if (response?.data && response?.data.length == 1) {
                  setCompanyCode(response?.data[0].companyCode);
                  formRef.current?.setFieldsValue({
                    companyCode: response?.data[0].companyCode
                  })
                }
                return response?.data?.map(item => ({value: item.companyCode, label: item.codeOrName}));
              },
              onChange: (value) => {
                setCompanyCode(value);
              },
              reloadKey: `${paymentMethodId}${soldToCode}`,
            }}
          />
        </Form.Item>
      )}
      {!!(PM_FIELD_CONFIG[paymentMethodId] & PREV_COLUMNS.SUPPLIER_OPT) && payToTypeId == Enum.PayToType.PAY_TO_SUPPLIER && (
        <Form.Item
          name="supplierId"
          label="供应商"
          rules={[{required: true, message: '供应商不能为空!'}]}>
          <WdiSelect {...{
            placeholder: "请选择",
            showSearch: true,
            remote: true,
            labelInValue: true,
            dataLoading: async (params) => {
              if (!companyCode) {
                return [];
              }
              const response = await Supplier.ajaxSearch(Object.assign({
                isActive: true,
                companyCode: companyCode,
              }, params));
              if (response?.data?.length == 1) {
                formRef.current?.setFieldsValue({supplierId: response?.data[0].supplierId,})
              }
              return response?.data?.map((item) => ({value: item.supplierId, label: item.codeOrName}));
            },
            reloadKey: companyCode,
          }} />
        </Form.Item>
      )}
      {!!(PM_FIELD_CONFIG[paymentMethodId] & PREV_COLUMNS.STAFF_OPT) && payToTypeId == Enum.PayToType.PAY_TO_STAFF && (
        <Form.Item
          name="payToUserId"
          label="员工"
          rules={[{required: true, message: '员工不能为空!'}]}>
          <WdiSelect {...{
            placeholder: "请选择员工",
            showSearch: true,
            remote: true,
            dataLoading: async (params) => {
              const response = await Staff.ajaxSearch(Object.assign({isActive: true}, params));
              return response?.data.map((item: any) => ({
                value: item.staffId,
                label: wdiCore.String.GenerateLabelDescription(item.staffNoOrName, item.title,),
              }));
            }
          }} />
        </Form.Item>
      )}
    </WdiModalForm>
  )
};
export default PaymentPrevApp;
